import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon, Menu, Label } from "semantic-ui-react";
import { Mixpanel } from "../../Screens/common/MixPanel";
import {
  User,
  currentReleaseVersion,
  enableDBCParserByDefault,
} from "../../../util";
import ByteBeamLogo from "../ByteBeamLogo";
import { DisplayIf } from "../../Screens/util";
import Toggle from "../Toggle";
import UserSummaryWithProjectPicker from "./UserSummaryWithProjectPicker";
import { ThemeContext } from "styled-components";
import { handleReleaseNoteClick } from "./DesktopNavBar";

type MobileNavBarProps = {
  readonly user: User;
  readonly activeItem: string;
  readonly setActiveItem: (item: string) => void;
  readonly currentTenant: string;
  readonly showSettingsTab: () => boolean;
  readonly handleThemeToggle: () => Promise<void>;
  readonly isMultiTenant: boolean;
  readonly setCurrentTenant: (tenant: string) => void;
  readonly setShowNewProjectModal: (show: boolean) => void;
  readonly getCurrentUser: () => Promise<void>;
};

type NavigationalTabProps = {
  readonly name: string;
  readonly title: string;
  readonly to: string;
  readonly shouldDisplay: boolean;
  readonly showTabsVisible?: boolean;
  readonly activeItem: string;
  readonly handleClick: () => void;
};

function NavigationalTab(props: NavigationalTabProps) {
  const {
    name,
    title,
    to,
    shouldDisplay,
    showTabsVisible,
    activeItem,
    handleClick,
  } = props;
  const isShowTabsVisible = showTabsVisible !== false;
  return (
    <DisplayIf cond={shouldDisplay && isShowTabsVisible}>
      <Menu.Item
        style={{
          display: "flex",
          paddingLeft: "4px",
        }}
        as={Link}
        to={to}
        name={name}
        active={activeItem === name}
        onClick={handleClick}
      >
        <Icon
          className={"angle right"}
          style={{
            float: "none",
            marginRight: "8px",
            marginLeft: "0px",
            fontSize: "18px",
          }}
        />
        <h4 style={{ fontSize: "14px", fontWeight: 700, marginTop: "0px" }}>
          {title.toUpperCase()}
        </h4>
      </Menu.Item>
    </DisplayIf>
  );
}

type NonNavigationalTabProps = {
  readonly name: string;
  readonly title: string;
  readonly shouldDisplay: boolean;
  readonly showTabsVisible?: boolean;
  readonly activeItem: string;
  readonly isOpen: boolean;
  readonly handleClick: () => void;
  readonly rightShift?: string;
};

function NonNavigationalTab(props: NonNavigationalTabProps) {
  const {
    name,
    title,
    shouldDisplay,
    showTabsVisible,
    activeItem,
    isOpen,
    handleClick,
    rightShift,
  } = props;
  const isShowTabsVisible = showTabsVisible !== false;
  return (
    <DisplayIf cond={shouldDisplay && isShowTabsVisible}>
      <Menu.Item
        style={{
          display: "flex",
          paddingLeft: "4px",
        }}
        name={name}
        active={activeItem === name}
        onClick={handleClick}
      >
        <Icon
          className={isOpen ? "angle down" : "angle right"}
          style={{
            float: "none",
            marginRight: "8px",
            marginLeft: rightShift ?? "0px",
            fontSize: "18px",
          }}
        />
        <h4 style={{ fontSize: "14px", fontWeight: 700, marginTop: "0px" }}>
          {title}
        </h4>
      </Menu.Item>
    </DisplayIf>
  );
}

type NestedNavigationalTabProps = {
  readonly name: string;
  readonly title: string;
  readonly to: string;
  readonly shouldDisplay: boolean;
  readonly activeItem: string;
  readonly handleClick: () => void;
  readonly icon: string;
  readonly rightShift?: string;
};

function NestedNavigationalTab(props: NestedNavigationalTabProps) {
  const {
    name,
    title,
    to,
    shouldDisplay,
    activeItem,
    handleClick,
    icon,
    rightShift,
  } = props;

  const location = useLocation();
  return (
    <DisplayIf cond={shouldDisplay}>
      <Menu.Item
        style={{
          display: "flex",
        }}
        as={Link}
        to={to}
        name={name}
        active={activeItem === name}
        onClick={() => {
          // TODO: Remove this hack once we have a better solution
          // =========================== Hack Starts ===========================
          let currentParentPath = location.pathname
            .split("/")
            .slice(0, -1)
            ?.join("/");
          let toParentPath = to.split("/").slice(0, -1)?.join("/");
          let isInventory =
            location.pathname.includes("inventory") || to.includes("inventory");
          let isActionSummary =
            location.pathname.includes("action-summary") ||
            to.includes("action-summary");
          if (
            toParentPath === currentParentPath ||
            (isInventory && isActionSummary)
          ) {
            window.location.href = to;
          }
          // ============================ Hack Ends ============================
          handleClick();
        }}
      >
        <Icon
          className={icon}
          style={{
            float: "none",
            marginRight: "12px",
            marginLeft: rightShift ?? "18px",
          }}
        />
        <h4
          style={{
            fontSize: "14px",
            fontWeight: 600,
            marginTop: "0px",
          }}
        >
          {title}
        </h4>
      </Menu.Item>
    </DisplayIf>
  );
}

function MobileNavBar(props: MobileNavBarProps) {
  const {
    user,
    activeItem,
    setActiveItem,
    currentTenant,
    showSettingsTab,
    handleThemeToggle,
    isMultiTenant,
    setCurrentTenant,
    setShowNewProjectModal,
    getCurrentUser,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isAlertsOpen, setIsAlertsOpen] = useState<boolean>(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState<boolean>(false);

  const permissions = user?.role?.permissions || {};
  const showTabs = user?.["tenant-settings"]?.show_tabs;

  const themeContext = useContext(ThemeContext);
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  useEffect(() => {
    setIsDarkTheme(themeContext?.name?.toLowerCase() === "dark");
  }, [themeContext]);

  return (
    <Menu
      className="main-navbar"
      fixed="top"
      borderless
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        height: isMenuOpen ? "100vh" : "auto",
        overflowY: "auto",
      }}
    >
      <Menu
        className="main-navbar mob-menu"
        fluid
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "62px",
        }}
      >
        <Menu.Item
          position="left"
          style={{
            padding: "12px 24px",
          }}
          className="logo"
          as={Link}
          to={
            permissions.showDeviceManagementTab
              ? `/projects/${currentTenant}/device-management/devices`
              : `/projects/${currentTenant}/dashboards`
          }
          onClick={() =>
            permissions.showDeviceManagementTab
              ? setActiveItem("device-management")
              : setActiveItem("dashboards")
          }
        >
          <ByteBeamLogo
            height="30px"
            user={user}
            currentTenant={currentTenant}
          />
        </Menu.Item>

        <Menu.Item position="right">
          <Icon
            name={isMenuOpen ? "close" : "bars"}
            size="large"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              margin: 0,
            }}
          />
        </Menu.Item>
      </Menu>

      {isMenuOpen && (
        <Menu
          vertical
          fluid
          borderless
          className="main-navbar"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
            minHeight: "100vh",
          }}
        >
          <div>
            <UserSummaryWithProjectPicker
              currentTenant={currentTenant}
              setCurrentTenant={setCurrentTenant}
              isMultiTenant={isMultiTenant}
              setShowNewProjectModal={setShowNewProjectModal}
            />

            {/* Device Management Link  */}
            <NavigationalTab
              name="device-management"
              title="DEVICE MANAGEMENT"
              to={`/projects/${currentTenant}/device-management/devices`}
              shouldDisplay={permissions.showDeviceManagementTab}
              showTabsVisible={showTabs?.device_management?.show}
              activeItem={activeItem}
              handleClick={() => {
                setActiveItem("device-management");
                setIsMenuOpen(!isMenuOpen);
              }}
            />

            {/* Dashboards Link  */}
            <NavigationalTab
              name="dashboards"
              title="Dashboards"
              to={`/projects/${currentTenant}/dashboards`}
              shouldDisplay={permissions.showDashboardsTab}
              showTabsVisible={showTabs?.dashboards?.show}
              activeItem={activeItem}
              handleClick={() => {
                setActiveItem("dashboards");
                setIsMenuOpen(!isMenuOpen);
              }}
            />

            {/* Actions Link  */}
            <NonNavigationalTab
              name="actions"
              title="ACTIONS"
              shouldDisplay={permissions.showActionsTab}
              showTabsVisible={showTabs?.actions?.show}
              activeItem={activeItem}
              isOpen={isActionsOpen}
              handleClick={() => {
                setIsActionsOpen(!isActionsOpen);
                setActiveItem("actions");
              }}
            />

            {/* Actions Nested Link */}
            {isActionsOpen && (
              <>
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/actions/live-actions`}
                  name="Action Details"
                  title="Action Details"
                  shouldDisplay={permissions.allowedActions?.length > 0}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Actions Summary");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"tv"}
                />

                {/* Inventory Management Link */}
                <NonNavigationalTab
                  name="Inventory"
                  title="Inventory"
                  shouldDisplay={
                    permissions.viewFirmwares || permissions.viewDeviceConfigs
                  }
                  activeItem={activeItem}
                  isOpen={isInventoryOpen}
                  handleClick={() => {
                    setActiveItem("Inventory");
                    setIsInventoryOpen(!isInventoryOpen);
                  }}
                  rightShift="30px"
                />

                {/* Inventory Management Nested Link */}
                {isInventoryOpen && (
                  <>
                    <NestedNavigationalTab
                      to={`/projects/${currentTenant}/actions/inventory/firmware-files`}
                      name={"Firmware Files"}
                      title={"Firmware Files"}
                      shouldDisplay={permissions.viewFirmwares}
                      activeItem={activeItem}
                      handleClick={() => {
                        setActiveItem("Firmware Files");
                        setIsMenuOpen(!isMenuOpen);
                      }}
                      icon={"file alternate outline"}
                      rightShift="64px"
                    />
                    <NestedNavigationalTab
                      to={`/projects/${currentTenant}/actions/inventory/json-configurations`}
                      name={"JSON Configurations"}
                      title={"JSON Configurations"}
                      shouldDisplay={permissions.viewDeviceConfigs}
                      activeItem={activeItem}
                      handleClick={() => {
                        setActiveItem("JSON Configurations");
                        setIsMenuOpen(!isMenuOpen);
                      }}
                      icon={"code"}
                      rightShift="64px"
                    />
                    <NestedNavigationalTab
                      to={`/projects/${currentTenant}/actions/inventory/geofence-configurations`}
                      name={"Geofence Configurations"}
                      title={"Geofence Configurations"}
                      shouldDisplay={permissions.viewDeviceConfigs}
                      activeItem={activeItem}
                      handleClick={() => {
                        setActiveItem("Geofence Configurations");
                        setIsMenuOpen(!isMenuOpen);
                      }}
                      icon={"street view"}
                      rightShift="64px"
                    />
                  </>
                )}
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/actions/new-action`}
                  name="New Action"
                  title="New Action"
                  shouldDisplay={permissions.allowedActions?.length > 0}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Actions Summary");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"plus square outline"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/actions/action-settings`}
                  name={"Action Settings"}
                  title={"Action Settings"}
                  shouldDisplay={permissions.viewActionTypes}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Action Settings");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"sitemap"}
                />
              </>
            )}

            {/* Alerts Link  */}
            <NonNavigationalTab
              name="alerts"
              title="ALERTS"
              shouldDisplay={user.role.name === "admin"}
              showTabsVisible={
                (permissions.viewAlerts || permissions.viewNotifications) &&
                showTabs?.alerts?.show !== false
              }
              activeItem={activeItem}
              isOpen={isAlertsOpen}
              handleClick={() => {
                setActiveItem("alerts");
                setIsAlertsOpen(!isAlertsOpen);
                // setIsMenuOpen(!isMenuOpen);
              }}
            />

            {isAlertsOpen && (
              <>
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/alerts/alerts-dashboard`}
                  name={"Alerts Dashboard"}
                  title={"Alerts Dashboard"}
                  shouldDisplay={permissions.viewAlerts}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Alerts Dashboard");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"list alternate outline"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/alerts/alert-rules`}
                  name={"Alert Rules"}
                  title={"Alert Rules"}
                  shouldDisplay={permissions.viewAlerts}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Alert Rules");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"file alternate outline"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/alerts/alert-notification`}
                  name={"Notification Settings"}
                  title={"Notification Settings"}
                  shouldDisplay={permissions.viewNotifications}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Notification Settings");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"code"}
                />
              </>
            )}

            {/* DBC Parsers Link */}
            <NavigationalTab
              name="DBC Parsers"
              title="DBC Parsers"
              to={`/projects/${currentTenant}/settings/dbc-parsers`}
              shouldDisplay={
                permissions.viewDBCParsers &&
                (showTabs?.dbc_parsers?.show || enableDBCParserByDefault())
              }
              activeItem={activeItem}
              handleClick={() => {
                setActiveItem("DBC Parsers");
                setIsMenuOpen(!isMenuOpen);
              }}
            />

            {/* Settings Link  */}
            <NonNavigationalTab
              name="settings"
              title="SETTINGS"
              shouldDisplay={showSettingsTab()}
              activeItem={activeItem}
              isOpen={isSettingsOpen}
              handleClick={() => {
                setIsSettingsOpen(!isSettingsOpen);
                setActiveItem("settings");
              }}
            />

            {/* Settings Nested Link */}
            {isSettingsOpen && (
              <>
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/general`}
                  name={"General"}
                  title={"General"}
                  shouldDisplay={user.role.permissions.editTenantSettings}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("General");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"key"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/users`}
                  name={"Users"}
                  title={"Users"}
                  shouldDisplay={permissions.viewUsers}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Users");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"users"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/streams`}
                  name="Streams"
                  title="Streams"
                  shouldDisplay={permissions.viewStreams}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Streams");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"table"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/roles`}
                  name="Roles"
                  title="Roles"
                  shouldDisplay={permissions.viewRoles}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Roles");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"settings"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/metadata`}
                  name={"Metadata"}
                  title={"Metadata"}
                  shouldDisplay={permissions.viewMetadataKeys}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Metadata");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"hdd"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/api_keys`}
                  name={"API Keys"}
                  title={"API Keys"}
                  shouldDisplay={user.role.name === "admin"}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("API Keys");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"key"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/session_types`}
                  name={"Session Types"}
                  title={"Session Types"}
                  shouldDisplay={
                    user.role.name === "admin" &&
                    showTabs?.alerts?.show !== false
                  }
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Session Types");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"history"}
                />
                <NestedNavigationalTab
                  to={`/projects/${currentTenant}/settings/device_components`}
                  name="Device Components"
                  title="Device Components"
                  shouldDisplay={showTabs?.device_components?.show === true}
                  activeItem={activeItem}
                  handleClick={() => {
                    setActiveItem("Device Components");
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  icon={"object group outline"}
                />
              </>
            )}
          </div>
          <div style={{ padding: "48px 0px 16px 0px" }}>
            <Menu.Item
              style={{ display: "flex", marginright: "12px" }}
              onClick={() => {
                setActiveItem("user");
                handleReleaseNoteClick(user, getCurrentUser);
              }}
            >
              <Label color="blue" style={{ marginRight: "8px" }}>
                {currentReleaseVersion}
              </Label>
              What's New!
            </Menu.Item>

            <Menu.Item>
              <Icon
                name="adjust"
                style={{ float: "none", marginRight: "12px" }}
              />
              Dark Mode{" "}
              <Toggle
                id="theme-toggle"
                size="medium"
                style={{
                  marginLeft: "12px",
                  top: "4px",
                }}
                checked={isDarkTheme}
                onClick={() => handleThemeToggle()}
              />
            </Menu.Item>

            <Menu.Item
              onClick={() => {
                Mixpanel.track("Logout");
                window.location.href = "/logout";
              }}
            >
              <Icon
                name="power off"
                style={{ float: "none", marginRight: "12px" }}
              />
              Logout
            </Menu.Item>
          </div>
        </Menu>
      )}
    </Menu>
  );
}

export default MobileNavBar;
